import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Spinner from 'src/components/Spinner/Spinner';
import HelpCircleMUI6 from 'src/components/HelpCircleMUI6';
import InputStartAdornment from 'src/views/management/ChatView/NewMessage/InputStartAdornment';
import InputEndAdornment from 'src/views/management/ChatView/NewMessage/InputEndAdornment';
import AttachedFilePrewiew from 'src/views/management/ChatView/NewMessage/AttachedFilePrewiew';
import AttachmentPendingTile from 'src/components/AttachmentPendingTile';
import TemplateAndDynamicFieldSelector from 'src/views/management/ChatView/modals/TemplateAndDynamicFieldSelector';
import CharacterCounter from 'src/views/management/ChatView/styled/CharacterCounter';
import {
  sendOrEditScheduledMessage,
  setCurrentMessage,
  setFileUploadToMessage,
  getFollowUpMessages,
  clearFollowUpMessages,
} from 'src/actions/chatActions';
import { addNotification } from 'src/actions/notificationActions';
import helpers from 'src/helpers';
import {
  NOTIFICATION_STATUS,
  STORAGE,
  STYLE_OPTIONS,
} from 'src/constants';
import { isCompressibleImage } from 'src/utils/imageCompression';
import { MIXED_CONTAINER_MESSAGE, tooManyFilesMessage, ATTACHMENTS_BUSY_MESSAGE } from 'src/helpers/attachmentBudget';
import { rebalanceAttachments } from 'src/helpers/attachmentRebalance';
import {
  containerForFile,
  templateDropsAttachments,
  TEMPLATE_REPLACED_ATTACHMENTS_MESSAGE,
  templateAttachmentsForApply,
} from 'src/helpers/contactCardAttachments';

const isMessageEdit = true;

const VALIDATE_MESSAGE = {
  validateFileSize: 'File size must be less than 300Kb',
  validateFileType: 'Must be a valid image, video, or audio file',
};

function ModalEditScheduledMessage({
  isModalVisible,
  chatKey,
  maxTextMessageLength,
  onCloseModal,
}) {
  const [messageCursorPosition, setMessageCursorPosition] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [insertMode, setInsertMode] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    user, azAccountName, databaseBeingUsed, mmsAttachmentPolicy,
  } = useSelector((state) => state?.account || {});
  const {
    currentMessage,
    followUpMessages,
    isMessageSending,
    isMessageUpdating,
  } = useSelector((state) => state?.chat || {});
  const {
    id,
    scheduledAt,
    body,
    attachments,
    messageAttachmentContainer,
    fileToReplace = false,
  } = currentMessage || {};
  const storedAttachments = (attachments || []).filter(Boolean);
  const [attachment] = storedAttachments;
  const { dynamicFields } = user || {};

  /* CON-1925 — the 2nd..Nth stored files, shown and editable only inside
   * the beta. Outside it the modal is the single-attachment modal it was,
   * posts the legacy single `attachment`, and the server keeps the stored
   * list whole when that value is still the first file. */
  const canSendMultiple = helpers.permissions.canSendMultipleAttachments(user);
  const maxAttachments = canSendMultiple
    ? Math.max(1, Number(mmsAttachmentPolicy?.maxAttachments) || 1)
    : 1;
  const mmsTotalBytes = Number(mmsAttachmentPolicy?.totalBytes) || 307200;
  const [extraAttachments, setExtraAttachments] = useState([]);
  // CON-1929 — originals of files picked in this window, by blob name, so a
  // rebalance recompresses from them. Stored files are fetched back instead.
  const originalsRef = useRef(new Map());
  // CON-1930 — beta: placeholders and a lock while files are being added.
  const [pendingTiles, setPendingTiles] = useState(0);
  const addingFilesRef = useRef(false);
  useEffect(() => {
    setExtraAttachments(canSendMultiple ? storedAttachments.slice(1, maxAttachments) : []);
    // Re-seed when a different message is opened, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isModalVisible]);
  const {
    followUpMessageList,
    loading: isFollowUpMessagesLoading,
  } = followUpMessages || {};
  const dispatch = useDispatch();

  const initialValues = {
    messageBody: body || '',
    attachment: attachment || '',
    messageAttachmentContainer: messageAttachmentContainer || null,
  };

  const validationSchema = Yup.object().shape({
    messageBody: Yup.string().max(maxTextMessageLength),
    attachment: Yup.string()
      .test('invalidAttachmentSize', VALIDATE_MESSAGE.validateFileSize, () => helpers.azure.validateFileSize(fileToReplace))
      .test('invalidAttachmentType', VALIDATE_MESSAGE.validateFileType, () => helpers.azure.validateFileType(fileToReplace)),
  });

  const fileWasNotUpload = () => {
    dispatch(addNotification('file upload', 'Your file wasn\'t uploaded', NOTIFICATION_STATUS.ERROR));
  };

  const fileWasNotDelete = () => {
    dispatch(addNotification('file delete', 'Your file wasn\'t deleted', NOTIFICATION_STATUS.ERROR));
  };

  const handleClose = () => {
    onCloseModal();
    setInsertMode('');
    dispatch(setCurrentMessage({}));
  };

  const onSubmit = async (values, {
    resetForm, setStatus, setSubmitting
  }) => {
    setSubmitting(true);
    const doWhenTry = () => {
      resetForm();
      setStatus({ success: true });
      setSubmitting(false);
      handleClose();
    };
    const doWhenCatch = () => {
      setStatus({ success: false });
      setSubmitting(false);
    };

    // Determining the attachment container based on file changes:
    // - If the file was deleted (fileToReplace === null), set container to null.
    // - If a new file was attached (fileToReplace is an object), generate a new container name.
    // - Otherwise, keep the existing container.
    const attachmentContainerToSend = fileToReplace === null
      ? null
      : fileToReplace
        ? helpers.azure.getUserContainerName(databaseBeingUsed, user.id)
        : values.messageAttachmentContainer;

    const newValuesAttachment = await helpers.azure.loadFileToAzureAndGetStatusCode(fileToReplace, attachment, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, attachmentContainerToSend, fileWasNotUpload, fileWasNotDelete, false, 'EO-ID', false);
    if (newValuesAttachment === null) {
      dispatch(addNotification('saveFile', 'There are some problems with Azure service.', NOTIFICATION_STATUS.ERROR));
      return;
    }
    if (helpers.isEmpty(values.messageBody) && (!newValuesAttachment || helpers.isEmpty(newValuesAttachment))) {
      dispatch(addNotification('sendFile', 'You must enter either a text message or select an image.', NOTIFICATION_STATUS.ERROR));
      return;
    }
    const attachmentFromMessage = !helpers.isEmpty(values.attachment) && fileToReplace !== null ? values.attachment : null;
    const attachmentToSend = !helpers.isEmpty(newValuesAttachment) ? newValuesAttachment : attachmentFromMessage;

    const sendData = {
      id,
      scheduledAt,
      body: values.messageBody?.trim() ? values.messageBody : null,
      attachment: attachmentToSend,
      // CON-1925 — inside the beta the full list is explicit, so removing a
      // file here really removes it. Outside the beta the key is absent and
      // the request is the single-value one it always was.
      ...(canSendMultiple
        ? { attachments: [attachmentToSend, ...extraAttachments].filter(Boolean) }
        : {}),
      messageAttachmentContainer: attachmentContainerToSend
    };
    dispatch(sendOrEditScheduledMessage(sendData, chatKey, doWhenTry, doWhenCatch));
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });
  const {
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    isSubmitting,
    values,
    errors,
  } = formik;

  const isLoading = isSubmitting || isMessageSending || isMessageUpdating || isFollowUpMessagesLoading;
  const isMessageIncludesBlanks = values?.messageBody && values.messageBody?.includes('__________');

  const saveMessageCursorPosition = (event) => {
    setMessageCursorPosition(event?.target?.selectionStart);
  };

  const handleOpenPopover = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };

  const handleOpenDynamicFieldSelect = () => {
    if (insertMode === 'dynamicFields') {
      setInsertMode('');
    } else {
      setInsertMode('dynamicFields');
    }
    handleClosePopover();
  };

  const handleOpenTemplateSelect = () => {
    if (insertMode === 'templates') {
      setInsertMode('');
    } else {
      setInsertMode('templates');
    }
    handleClosePopover();
  };

  const { getInputProps, open: handleOpenAttachFile } = useDropzone({
    maxFiles: maxAttachments,
    multiple: maxAttachments > 1,
    noClick: true,
    noKeyboard: true,
    noDrag: true,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'video/*': ['.mp4', '.webm', '.flv', '.mov', '.wmv', '.avi', '.mkv', '.3gp'],
      'audio/*': ['.mp3', '.ogg', '.wav', '.flac', '.amr', '.aac', '.wma', '.m4a']
    },
  });

  const updateFileUploadToMessage = (value) => {
    /* CON-1925 — removing the first of several promotes the next one. The
     * promoted file is already a blob, so fileToReplace is cleared (false),
     * never set to the delete sentinel (null). */
    if (value === null && extraAttachments.length) {
      const [next, ...rest] = extraAttachments;
      dispatch(setFileUploadToMessage(false, isMessageEdit));
      setFieldValue('attachment', next, false);
      setFieldValue(
        'messageAttachmentContainer',
        values.messageAttachmentContainer || helpers.azure.getUserContainerName(databaseBeingUsed, user.id),
        false,
      );
      setExtraAttachments(rest);
      return;
    }
    dispatch(setFileUploadToMessage(value, isMessageEdit));
    setFieldValue('attachment', '', false);
    setFieldValue('messageAttachmentContainer', null, false);
  };
  const removeExtraAttachment = (index) => {
    setExtraAttachments(extraAttachments.filter((_, i) => i !== index));
  };
  // Called by TemplateAndDynamicFieldSelector after it has put the template's
  // text, attachment 1 and container in place (CON-1936: it used to never be
  // called, so the old files 2..N stayed beside the template's file).
  const handleTemplateSelect = (template) => {
    // CON-1936 — the template's own files 2..N come with it (beta).
    const templateFiles = templateAttachmentsForApply({ template, maxAttachments });
    // CON-1934 — in the beta, say so when the template takes files away.
    if (templateDropsAttachments({
      maxAttachments,
      current: [values.attachment, ...extraAttachments],
      templateAttachment: template.attachment || null,
      templateExtras: templateFiles.extras,
    })) {
      dispatch(addNotification('template-attachments', TEMPLATE_REPLACED_ATTACHMENTS_MESSAGE, NOTIFICATION_STATUS.INFO));
    }
    // CON-1925 — a template replaces every attachment, not just the first.
    setExtraAttachments(templateFiles.extras);
  };
  const validateFileInput = (uploadFileData) => {
    // Skip size validation for compressible images - compression will handle it in azure.js
    const isImage = uploadFileData?.file && isCompressibleImage(uploadFileData.file);
    if (!isImage && !helpers.azure.validateFileSize(uploadFileData)) {
      dispatch(addNotification('attachment-size', VALIDATE_MESSAGE.validateFileSize, NOTIFICATION_STATUS.ERROR));
      return false;
    }
    if (!helpers.azure.validateFileType(uploadFileData)) {
      dispatch(addNotification('attachment-type', VALIDATE_MESSAGE.validateFileType, NOTIFICATION_STATUS.ERROR));
      return false;
    }
    return true;
  };

  /* CON-1925 / CON-1929 — add files in the beta. Every add re-plans the
   * whole message and recompresses whatever is over its share (see
   * helpers/attachmentRebalance.js). A first file still waiting to upload
   * (fileToReplace) is planned with the rest and uploaded now. State is
   * passed in rather than read from the closure, because a pick of several
   * files lands before Redux and Formik have caught up. Returns true when
   * every file ended up on the message. */
  const addExtraFilesNow = async (files, {
    pendingFirst = fileToReplace?.file || null,
    firstName = values.attachment,
    extras = extraAttachments,
  } = {}) => {
    const existingNames = [pendingFirst ? null : firstName, ...extras].filter(Boolean);
    const ownContainer = helpers.azure.getUserContainerName(databaseBeingUsed, user.id);
    if (values.messageAttachmentContainer && values.messageAttachmentContainer !== ownContainer) {
      dispatch(addNotification('attachment-mixed', MIXED_CONTAINER_MESSAGE, NOTIFICATION_STATUS.ERROR));
      return false;
    }
    const room = maxAttachments - existingNames.length - (pendingFirst ? 1 : 0);
    if (room <= 0) {
      dispatch(addNotification('attachment-count', tooManyFilesMessage(maxAttachments), NOTIFICATION_STATUS.ERROR));
      return false;
    }
    let filesToAdd = files;
    if (files.length > room) {
      dispatch(addNotification('attachment-count', tooManyFilesMessage(maxAttachments), NOTIFICATION_STATUS.WARNING));
      filesToAdd = files.slice(0, room);
    }
    const uploads = filesToAdd.map((file) => ({ file, value: file.name }));
    if (uploads.some((uploadFileData) => !validateFileInput(uploadFileData))) {
      return false;
    }
    const containerName = values.messageAttachmentContainer || ownContainer;
    const result = await rebalanceAttachments({
      existing: existingNames.map((name) => ({ name, original: originalsRef.current.get(name) })),
      newFiles: [...(pendingFirst ? [pendingFirst] : []), ...filesToAdd],
      totalBytes: mmsTotalBytes,
      deps: {
        getUrl: (name) => helpers.azure.getImageFileURL(name, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, containerForFile(name, containerName), true,),
        upload: (file) => helpers.azure.loadFileToAzureAndGetStatusCode(
          { file, value: file.name },
          null,
          user.id,
          azAccountName,
          STORAGE.sas,
          databaseBeingUsed,
          ownContainer,
          fileWasNotUpload,
          fileWasNotDelete,
          false,
          'EO-ID',
          true,
        ),
      },
    });
    if (!result.ok) {
      dispatch(addNotification('attachment-size', result.error, NOTIFICATION_STATUS.ERROR));
      return false;
    }
    result.originals.forEach((file, name) => originalsRef.current.set(name, file));
    // Result order is existing first, then new. A pending first file was
    // planned as new, so move it back to the front.
    let ordered = result.names;
    if (pendingFirst) {
      const pendingIndex = existingNames.length;
      ordered = [result.names[pendingIndex], ...result.names.slice(0, pendingIndex),
        ...result.names.slice(pendingIndex + 1)];
      dispatch(setFileUploadToMessage(false, isMessageEdit));
    }
    const [first, ...rest] = ordered;
    setFieldValue('attachment', first, false);
    setFieldValue('messageAttachmentContainer', ownContainer, false);
    setExtraAttachments(rest);
    return true;
  };

  /* CON-1930 — the in-progress state around every beta add: one placeholder
   * per file, Save and the + menu disabled, and a second pick turned away
   * until the first is done. */
  const addExtraFiles = async (files, options) => {
    if (addingFilesRef.current) {
      dispatch(addNotification('attachment-busy', ATTACHMENTS_BUSY_MESSAGE, NOTIFICATION_STATUS.INFO));
      return false;
    }
    addingFilesRef.current = true;
    setPendingTiles(Math.max(0, Math.min(files.length, maxAttachments)));
    try {
      return await addExtraFilesNow(files, options);
    } finally {
      addingFilesRef.current = false;
      setPendingTiles(0);
    }
  };
  const isAddingFiles = pendingTiles > 0;

  const handleOnSelectedFile = async (event) => {
    const { target: { files, value } } = event;
    if (!files || !value) {
      return;
    }
    const picked = Array.from(files);
    // CON-1925 — with something attached and room left, add; otherwise the
    // single-file replace this modal has always done.
    const hasSomething = !!values.attachment || !!fileToReplace;
    if (maxAttachments > 1 && hasSomething) {
      await addExtraFiles(picked);
      handleClosePopover();
      return;
    }
    // CON-1929 — several picked onto an empty message: plan them together.
    if (maxAttachments > 1 && picked.length > 1) {
      await addExtraFiles(picked, { pendingFirst: null, firstName: '', extras: [] });
      handleClosePopover();
      return;
    }
    const uploadFileData = {
      file: picked[0],
      value
    };
    if (validateFileInput(uploadFileData)) {
      dispatch(setFileUploadToMessage(uploadFileData, isMessageEdit));
    }
    handleClosePopover();
  };

  const onBlurInput = (event) => {
    handleBlur(event);
    saveMessageCursorPosition(event);
  };

  const handleSetFocusField = useCallback(() => {
    const inputElements = document.getElementsByName('messageBody');
    if (inputElements.length > 0) {
      inputElements[0].focus(); // {preventScroll: true} doesn't work

      if (typeof messageCursorPosition === 'number') {
        // Set the cursor position for input
        inputElements[0].setSelectionRange(messageCursorPosition, messageCursorPosition);
      }
    }
  }, [messageCursorPosition]);

  const handleAddEmoji = (e) => {
    const emoji = e.native;
    const currentText = values.messageBody;
    // true when messageCursorPosition isn't number maybe false or null
    const isNullMessageCursorPosition = !messageCursorPosition && messageCursorPosition !== 0;
    const text = isNullMessageCursorPosition
      ? `${currentText}${emoji}`
      : `${currentText.substring(0, messageCursorPosition)}${emoji}${currentText.substring(messageCursorPosition)}`;
    const length = !isNullMessageCursorPosition ? messageCursorPosition + emoji.length : text.length;
    setFieldTouched('messageBody', true);
    setFieldValue('messageBody', text, true);
    setMessageCursorPosition(length);
  };

  useEffect(() => {
    if (isModalVisible) {
      dispatch(getFollowUpMessages());
    }

    return () => {
      dispatch(clearFollowUpMessages());
    };
  }, [dispatch, isModalVisible]);

  return (
    <Dialog
      open={isModalVisible}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: isMobile ? '24px 0' : '24px',
        },
      }}
    >
      <Box
        sx={{
          padding: '0 0 0 16px',
          textAlign: 'center',
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            padding: 0,
          }}
        >
          Update Message
        </DialogTitle>
        <Typography
          variant="subtitle1"
          color="#546E7A"
        >
          Update this scheduled message
        </Typography>
      </Box>
      <DialogContent>
        <form
          id="formEditScheduledMessage"
          onSubmit={handleSubmit}
        >
          <Spinner
            loading={isLoading}
            positionType="absolute"
          />
          {insertMode && (
            <TemplateAndDynamicFieldSelector
              insertMode={insertMode}
              dynamicFields={dynamicFields}
              templates={followUpMessageList}
              messageCursorPosition={messageCursorPosition}
              setMessageCursorPosition={setMessageCursorPosition}
              formik={formik}
              onTemplateSelect={handleTemplateSelect}
            />
          )}
          {(fileToReplace || values.attachment) && (
            <AttachedFilePrewiew
              fileName={helpers.azure.getImageFileURL(values.attachment, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, values.messageAttachmentContainer)}
              fileUpload={fileToReplace}
              isLoading={isLoading || isAddingFiles}
              validateFileInput={validateFileInput}
              setFileUploadToMessage={updateFileUploadToMessage}
            />
          )}
          {/* CON-1925 — the 2nd..Nth files, each with its own remove. */}
          {(extraAttachments.length > 0 || isAddingFiles) && (
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px'
            }}
            >
              {extraAttachments.map((extraName, index) => (
                <AttachedFilePrewiew
                  key={extraName}
                  fileName={helpers.azure.getImageFileURL(extraName, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, values.messageAttachmentContainer)}
                  isLoading={isLoading || isAddingFiles}
                  validateFileInput={() => true}
                  setFileUploadToMessage={() => removeExtraAttachment(index)}
                />
              ))}
              {/* CON-1930 — one placeholder per file still being added. */}
              {Array.from({ length: pendingTiles }, (_, index) => (
                <AttachmentPendingTile key={`pending-${index}`} />
              ))}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              minHeight: '24px',
              marginTop: '8px',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                color: '#F15642',
              }}
            >
              {isMessageIncludesBlanks && (
                <>
                  <Typography
                    variant="h5"
                  >
                    Please review!&nbsp;&nbsp;&nbsp;Includes blanks!
                  </Typography>
                  <HelpCircleMUI6
                    showInfo={(
                      <>
                        This may have happened because you used a template that has dynamic field(s) that cannot be used on this screen.
                        <br />
                        <br />
                        Review the blanks & fill them in, or use a different template.
                      </>
                    )}
                  />
                </>
              )}
            </Box>
            <CharacterCounter
              currentLength={(values?.messageBody || '').length}
              maxLength={maxTextMessageLength}
              isError={Boolean(errors?.messageBody)}
              isMobile={isMobile}
            />
          </Box>
          <OutlinedInput
            name="messageBody"
            error={Boolean(errors?.messageBody)}
            color="secondary"
            placeholder="Write something..."
            onChange={handleChange}
            onBlur={onBlurInput}
            value={values.messageBody}
            disabled={isLoading}
            size="small"
            minRows={8}
            maxRows={16}
            startAdornment={(
              <InputStartAdornment
                isLoading={isLoading || isAddingFiles}
                handleOpenPopover={handleOpenPopover}
                handleClosePopover={handleClosePopover}
                popoverAnchorEl={popoverAnchorEl}
                openUploadDialog={handleOpenAttachFile}
                openDynamicFieldsDialog={handleOpenDynamicFieldSelect}
                openMessageTemplateDialog={handleOpenTemplateSelect}
                hasAttachment={!!values.attachment || !!fileToReplace}
                canAddAnotherFile={maxAttachments > 1
                  && ([values.attachment, ...extraAttachments].filter(Boolean).length + (fileToReplace ? 1 : 0)) < maxAttachments}
              />
            )}
            endAdornment={!isMobile && (
              <InputEndAdornment
                isLoading={isLoading}
                handleAddEmoji={handleAddEmoji}
                handleSetFocusField={handleSetFocusField}
              />
            )}
            sx={{
              paddingLeft: '8px',
              backgroundColor: isMessageIncludesBlanks ? '#f0f0f0' : undefined
            }}
            fullWidth
            multiline
          />
          <input {...getInputProps({ onChange: handleOnSelectedFile })} />
        </form>
      </DialogContent>
      <DialogActions
        sx={{
          padding: 0,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY_REVERSED,
            minWidth: '105px',
            marginRight: '14px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={(!values.messageBody?.trim() && !fileToReplace && !values.attachment && !extraAttachments.length) || isLoading || isAddingFiles || Boolean(errors?.messageBody)}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY,
            minWidth: '105px',
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalEditScheduledMessage.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  maxTextMessageLength: PropTypes.number.isRequired,
  chatKey: PropTypes.string.isRequired,
  onCloseModal: PropTypes.func.isRequired,
};

export default ModalEditScheduledMessage;
